/**
 * Awwwards-Worthy Animations
 * Stack: Lenis (Smooth Scroll) + GSAP (ScrollTrigger)
 */

document.addEventListener("DOMContentLoaded", () => {
    // Track whether any dialog is open (read by the wheel blocker below)
    let isDialogOpen = false;

    // Register this BEFORE new Lenis() so it runs first in the window bubble chain.
    // Lenis listens on window (bubble) and calls preventDefault() even when stopped.
    // stopImmediatePropagation() prevents Lenis's listener from firing for events
    // that originate inside an open <dialog>, allowing native overflow-y scroll to work.
    window.addEventListener('wheel', (e) => {
        if (isDialogOpen && e.target?.closest?.('dialog[open]')) {
            e.stopImmediatePropagation();
        }
    }, { passive: false });

    // 1. Initialize Lenis Smooth Scroll (registers its wheel listener AFTER ours)
    const lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        direction: 'vertical',
        gestureDirection: 'vertical',
        smooth: true,
        mouseMultiplier: 1,
        smoothTouch: false,
        touchMultiplier: 2,
    });

    // Synchronize Lenis with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    // Stop/start Lenis on dialog open/close (prevents Lenis from fighting body position:fixed).
    // DialogOpenEvent/CloseEvent are non-bubbling so we use capture phase.
    let openDialogCount = 0;
    document.addEventListener('dialog:open', () => {
        openDialogCount++;
        isDialogOpen = true;
        lenis.stop();
    }, { capture: true });
    document.addEventListener('dialog:close', () => {
        openDialogCount = Math.max(0, openDialogCount - 1);
        if (openDialogCount === 0) {
            isDialogOpen = false;
            lenis.start();
        }
    }, { capture: true });

    // 2. Register GSAP Plugins
    gsap.registerPlugin(ScrollTrigger);

    // 3. Global Reveal Animations
    initRevealAnimations();

    // 4. Collection-Specific Animations
    initCollectionAnimations();
});

function initRevealAnimations() {
    // Text Reveal (Slide up with clip-path)
    const splitTypes = document.querySelectorAll('.reveal-text');

    splitTypes.forEach((char, i) => {
        const text = new SplitType(char, { types: 'chars,words' });

        gsap.from(text.chars, {
            scrollTrigger: {
                trigger: char,
                start: 'top 80%',
                end: 'top 20%',
                scrub: false,
                markers: false
            },
            y: 100,
            opacity: 0,
            stagger: 0.02,
            duration: 1,
            ease: 'power4.out',
            onComplete: () => {
                // Optional cleanup or callback
            }
        });
    });

    // Image Parallax
    const parallaxImages = document.querySelectorAll('[data-parallax]');
    parallaxImages.forEach(img => {
        gsap.to(img, {
            yPercent: -20,
            ease: "none",
            scrollTrigger: {
                trigger: img.parentElement,
                start: "top bottom",
                end: "bottom top",
                scrub: true
            }
        });
    });
    // Generic Fade Up
    const fadeElements = document.querySelectorAll('.reveal-up');
    fadeElements.forEach(el => {
        gsap.from(el, {
            scrollTrigger: {
                trigger: el,
                start: 'top 85%',
                toggleActions: 'play none none reverse'
            },
            y: 40,
            opacity: 0,
            duration: 1,
            ease: 'power3.out'
        });
    });

    // Hero Specific Parallax (Smoother, subtle)
    const heroParallax = document.querySelectorAll('[data-parallax-hero]');
    heroParallax.forEach(bg => {
        gsap.to(bg, {
            yPercent: 15, // Move DOWN slightly to create depth (background moves slower than foreground)
            ease: "none",
            scrollTrigger: {
                trigger: bg.parentElement, // Trigger based on section
                start: "top top", // Start when section top is at viewport top
                end: "bottom top",
                scrub: true
            }
        });
    });
}

/**
 * Collection-Specific Animations
 */
function initCollectionAnimations() {
    initCollectionHeroAnimation();
    initCollectionGridStagger();
    initCardParallax();
}

/**
 * Collection Hero Banner Animation
 */
function initCollectionHeroAnimation() {
    const hero = document.querySelector('[data-collection-hero]');
    if (!hero) return;

    // Mark hero as visible for CSS transitions
    gsap.to(hero, {
        scrollTrigger: {
            trigger: hero,
            start: 'top 80%',
            onEnter: () => hero.classList.add('is-visible'),
            once: true
        }
    });
}

/**
 * Staggered Product Grid Reveal
 */
function initCollectionGridStagger() {
    const cards = document.querySelectorAll('.collection-product-card');
    if (cards.length === 0) return;

    cards.forEach((card, index) => {
        gsap.to(card, {
            scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                onEnter: () => {
                    setTimeout(() => {
                        card.classList.add('is-revealed');
                    }, index * 50); // Stagger by 50ms per card
                },
                once: true
            }
        });
    });
}

/**
 * Parallax Effect Inside Product Cards
 * Product images move subtly within their containers on scroll
 */
function initCardParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const images = document.querySelectorAll('.collection-product-card__image[data-card-parallax]');

    images.forEach(img => {
        gsap.to(img, {
            yPercent: -8,
            ease: 'none',
            scrollTrigger: {
                trigger: img.closest('.collection-product-card'),
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
            }
        });
    });
}
